import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MenuModule } from 'primeng/menu';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { DialogModule } from 'primeng/dialog';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';
import { MenuItem } from 'primeng/api';
import { CalendarService, CalendarItem } from '../../../core/services/calendar.service';
import { Observable } from 'rxjs';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-calendars',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MenuModule,
    ButtonModule,
    InputTextModule,
    DialogModule,
    TooltipModule,
    ConfirmDialogModule,
    TranslocoPipe,
  ],
  providers: [ConfirmationService],
  templateUrl: './calendars.component.html',
  styleUrls: ['./calendars.component.scss'],
})
export class CalendarsComponent implements OnInit {
  visible = false;
  selectedCalendarType = 'personal';
  searchQuery = '';

  // New calendar form
  showNewCalendarForm = false;
  newCalendar = {
    name: '',
    color: '#3b82f6',
  };

  // Edit calendar
  editingCalendar: CalendarItem | null = null;

  menuItems: MenuItem[] = [];

  calendars$: Observable<CalendarItem[]>;
  calendars: CalendarItem[] = [];

  colorOptions = [
    { label: 'Soft Red', value: '#fcafaf' },
    { label: 'Soft Pink', value: '#f8c8dc' },
    { label: 'Peach', value: '#ffd8b1' },
    { label: 'Light Orange', value: '#ffe5a8' },
    { label: 'Soft Yellow', value: '#fff4b2' },
    { label: 'Pale Lime', value: '#e0f7c2' },
    { label: 'Mint Green', value: '#baf2c2' },
    { label: 'Light Teal', value: '#a8f0e6' },
    { label: 'Sky Blue', value: '#a8dfff' },
    { label: 'Baby Blue', value: '#b3ccff' },
    { label: 'Lavender', value: '#d9c7f2' },
    { label: 'Soft Purple', value: '#e5c8f8' },
  ];

  private calendarService = inject(CalendarService);
  private confirmationService = inject(ConfirmationService);
  private translocoService = inject(TranslocoService);

  constructor() {
    this.calendars$ = this.calendarService.calendars$;
  }

  ngOnInit() {
    this.calendars$.subscribe((calendars) => {
      this.calendars = calendars;
    });

    // Initialize menu items with translations
    this.menuItems = [
      {
        label: 'calendar.calendarsDialog.personal',
        icon: 'pi pi-user',
        command: () => this.selectCalendarType('personal'),
        data: { type: 'personal' },
      },
      {
        label: 'calendar.calendarsDialog.sync',
        icon: 'pi pi-sync',
        command: () => this.selectCalendarType('sync'),
        data: { type: 'sync' },
      },
      {
        label: 'calendar.calendarsDialog.organization',
        icon: 'pi pi-building',
        command: () => this.selectCalendarType('organization'),
        data: { type: 'organization' },
      },
      {
        label: 'calendar.calendarsDialog.teams',
        icon: 'pi pi-users',
        command: () => this.selectCalendarType('teams'),
        data: { type: 'teams' },
      },
      {
        label: 'calendar.calendarsDialog.sessions',
        icon: 'pi pi-calendar',
        command: () => this.selectCalendarType('sessions'),
        data: { type: 'sessions' },
      },
    ];

    this.selectCalendarType('personal');
  }

  selectCalendarType(type: string) {
    this.selectedCalendarType = type;
    this.searchQuery = '';
  }

  personalCalendars: CalendarItem[] = [];
  syncCalendars: CalendarItem[] = [];
  organizationCalendars: CalendarItem[] = [];
  teamsCalendars: CalendarItem[] = [];
  sessionsCalendars: CalendarItem[] = [];

  getCurrentCalendars(): CalendarItem[] {
    switch (this.selectedCalendarType) {
      case 'personal':
        return this.calendars;
      case 'sync':
        return this.syncCalendars;
      case 'organization':
        return this.organizationCalendars;
      case 'teams':
        return this.teamsCalendars;
      case 'sessions':
        return this.sessionsCalendars;
      default:
        return [];
    }
  }

  getFilteredCalendars(): CalendarItem[] {
    const calendars = this.getCurrentCalendars();
    if (!this.searchQuery.trim()) {
      return calendars;
    }

    return calendars.filter((calendar) =>
      calendar.name.toLowerCase().includes(this.searchQuery.toLowerCase()),
    );
  }

  onSearchInput() {
    // Search functionality is handled by getFilteredCalendars()
  }

  clearSearch() {
    this.searchQuery = '';
  }

  show() {
    this.visible = true;
  }

  hide() {
    this.visible = false;
    this.showNewCalendarForm = false;
    this.editingCalendar = null;
    this.resetNewCalendarForm();
  }

  // New Calendar Methods
  showNewCalendarFormDialog() {
    this.showNewCalendarForm = true;
    this.editingCalendar = null;
    this.resetNewCalendarForm();
  }

  showEditCalendarForm(calendar: CalendarItem) {
    this.editingCalendar = calendar;
    this.newCalendar = {
      name: calendar.name,
      color: calendar.color,
    };
    this.showNewCalendarForm = true;
  }

  resetNewCalendarForm() {
    this.newCalendar = {
      name: '',
      color: '#3b82f6',
    };
  }

  saveCalendar() {
    if (!this.newCalendar.name.trim()) {
      return;
    }

    if (this.editingCalendar) {
      // Update existing calendar
      this.calendarService.updateCalendar(this.editingCalendar.id, {
        name: this.newCalendar.name,
        color: this.newCalendar.color,
      });
    } else {
      // Create new calendar
      this.calendarService.addCalendar({
        name: this.newCalendar.name,
        color: this.newCalendar.color,
        visible: true,
      });
    }

    this.showNewCalendarForm = false;
    this.editingCalendar = null;
    this.resetNewCalendarForm();
  }

  cancelNewCalendar() {
    this.showNewCalendarForm = false;
    this.editingCalendar = null;
    this.resetNewCalendarForm();
  }

  deleteCalendar(calendar: CalendarItem) {
    this.confirmationService.confirm({
      message: this.translocoService.translate('calendar.calendarsDialog.confirmDelete.message', {
        name: calendar.name,
      }),
      header: this.translocoService.translate('calendar.calendarsDialog.confirmDelete.header'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translocoService.translate('calendar.calendarsDialog.confirmDelete.accept'),
      rejectLabel: this.translocoService.translate('calendar.calendarsDialog.confirmDelete.reject'),
      accept: () => {
        this.calendarService.deleteCalendar(calendar.id);
      },
    });
  }

  toggleCalendarVisibility(calendar: CalendarItem) {
    this.calendarService.toggleCalendarVisibility(calendar.id);
  }
}
